import { useMemo } from 'react';

import type {
  VBaseTableCellRenderer,
  VBaseTableColumnRenederer,
  VBaseTableProps,
} from '../VBaseTable';
import { useDefaultColumnList } from '../VBaseTable';
import { VFlex } from '../VFlex';
import { VIconButton } from '../VIconButton';
import { useVTheme } from '../VThemeProvider';
import { CheckBox, CheckBoxOutlineBlank, Delete } from '../asset';
import { joinCn } from '../util';
import FilterColumnRenderer from './FilterColumnRenderer';
import SorterColumnRenderer from './SorterColumnRenderer';
import { VTableContext } from './context';
import type { VTableContextValue, VTableData } from './type';

export const useVTableContext = <T extends VTableData>() =>
  VTableContext.hook() as VTableContextValue<T>;

export const useFilterableAndSortable = <T extends VTableData>({
  columnRenderer,
}: {
  columnRenderer: VBaseTableColumnRenederer<T>;
}) => {
  const { columnSorter, columnFilter } = useVTableContext();

  const enhancedColumnRenderer = useMemo(() => {
    const sortableColumnIds = new Set(columnSorter?.map(({ id }) => id));
    const filterableColumnIds = new Set(columnFilter?.map(({ id }) => id));
    const newColumnRenderer = {} as VBaseTableColumnRenederer<T>;

    Object.keys(columnRenderer || {}).forEach(key => {
      const typedKey = key as keyof T;
      if (columnRenderer?.[typedKey]) {
        newColumnRenderer[typedKey] = columnRenderer[typedKey];
      }
    });
    sortableColumnIds.forEach(columnId => {
      const typedColumnId = columnId as keyof T;
      newColumnRenderer[typedColumnId] = () => {
        return (
          <SorterColumnRenderer
            columnId={columnId}
            columnRenderer={columnRenderer ?? {}}
          />
        );
      };
    });

    filterableColumnIds.forEach(columnId => {
      const typedColumnId = columnId as keyof T;
      newColumnRenderer[typedColumnId] = () => {
        return (
          <FilterColumnRenderer
            columnId={columnId}
            columnRenderer={columnRenderer ?? {}}
          />
        );
      };
    });

    return newColumnRenderer;
  }, [columnSorter, columnFilter, columnRenderer]);

  return enhancedColumnRenderer;
};

export const useCheckable = <T extends VTableData>({
  columnList,
  columnRenderer,
  columnStyleMap,
  cellRenderer,
  data,
}: VBaseTableProps<T>) => {
  const { onRowCheckedChange, onAllRowCheckedChange, setRowChecked } =
    useVTableContext();

  const defaultColumnList = useDefaultColumnList(columnList, data);

  const enhancedColumnList = defaultColumnList?.includes('checked')
    ? defaultColumnList
    : ['checked', ...(defaultColumnList ?? [])];

  const getIcon = (checked: boolean) => {
    if (!checked) return CheckBoxOutlineBlank;
    return CheckBox;
  };

  const enhancedColumnRenderer = {
    ...columnRenderer,
    checked:
      columnRenderer?.checked == null
        ? () => {
            const isAllChecked = data.every(row => setRowChecked?.(row));
            const Icon = getIcon(isAllChecked);

            return (
              <VFlex className="size-full" justify="center" items="center">
                <Icon
                  className="size-5 text-primary-500"
                  role="button"
                  onClick={() => onAllRowCheckedChange?.(isAllChecked)}
                />
              </VFlex>
            );
          }
        : columnRenderer?.checked,
  };
  const enhancedColumnStyleMap = {
    ...columnStyleMap,
    checked:
      columnStyleMap?.checked == null
        ? {
            className: 'w-17',
          }
        : columnStyleMap?.checked,
  };
  const enhancedCellRenderer = {
    ...cellRenderer,
    checked:
      cellRenderer?.checked == null
        ? (_, __, row) => {
            const Icon = getIcon(setRowChecked?.(row) ?? false);
            const onClick = (e: React.MouseEvent<SVGSVGElement>) => {
              e.stopPropagation();
              onRowCheckedChange?.(row);
            };
            return (
              <VFlex className="size-full" justify="center" items="center">
                <Icon
                  className="size-5 text-primary-500"
                  role="button"
                  onClick={onClick}
                />
              </VFlex>
            );
          }
        : cellRenderer?.checked,
  } as VBaseTableCellRenderer<T>;

  if (!setRowChecked) {
    return {
      columnList: columnList ?? [],
      columnRenderer: columnRenderer ?? {},
      columnStyleMap: columnStyleMap ?? {},
      cellRenderer: cellRenderer ?? {},
    };
  }

  return {
    columnList: enhancedColumnList,
    columnRenderer: enhancedColumnRenderer as VBaseTableColumnRenederer<T>,
    columnStyleMap: enhancedColumnStyleMap,
    cellRenderer: enhancedCellRenderer,
  };
};

export const useRemovable = <T extends VTableData>({
  columnList,
  columnRenderer,
  columnStyleMap,
  cellRenderer,
  data,
}: VBaseTableProps<T>) => {
  const { setRowRemovable, onRemove } = useVTableContext();

  const defaultColumnList = useDefaultColumnList(columnList, data);

  const enhancedColumnList = defaultColumnList?.includes('remove')
    ? defaultColumnList
    : [...(defaultColumnList ?? []), 'remove'];

  const enhancedColumnRenderer = {
    ...columnRenderer,
    remove: columnRenderer?.remove == null ? 'remove' : columnRenderer?.remove,
  };
  const enhancedColumnStyleMap = {
    ...columnStyleMap,
    remove:
      columnStyleMap?.remove == null
        ? {
            className: 'w-17',
          }
        : columnStyleMap?.remove,
  };
  const enhancedCellRenderer = {
    ...cellRenderer,
    remove:
      cellRenderer?.remove == null
        ? (_, __, row) => {
            return (
              <VFlex className="size-full" justify="center" items="center">
                <VIconButton
                  Icon={Delete}
                  size="sm"
                  variant="roundDanger"
                  onClick={e => {
                    e.stopPropagation();
                    onRemove?.(row.id);
                  }}
                  disabled={!setRowRemovable?.(row)}
                />
              </VFlex>
            );
          }
        : columnRenderer?.remove,
  } as VBaseTableCellRenderer<T>;

  if (!setRowRemovable) {
    return {
      columnList: columnList ?? [],
      columnRenderer: columnRenderer ?? {},
      columnStyleMap: columnStyleMap ?? {},
      cellRenderer: cellRenderer ?? {},
    };
  }

  return {
    columnList: enhancedColumnList,
    columnRenderer: enhancedColumnRenderer as VBaseTableColumnRenederer<T>,
    columnStyleMap: enhancedColumnStyleMap,
    cellRenderer: enhancedCellRenderer,
  };
};

export const useRowClassName = <T extends VTableData>() => {
  const { themeName } = useVTheme();
  const { setRowActivated, setRowChecked, setRowSelected } = useVTableContext();

  const rowClassName = (row: T) => {
    const isChecked = setRowChecked?.(row);
    const isActivated = setRowActivated?.(row);
    const setIsSelectableExist = !!setRowSelected;
    const isSelectable = setIsSelectableExist && setRowSelected(row);
    const isDisabled = setIsSelectableExist && !setRowSelected(row);

    return joinCn({
      '!cursor-not-allowed': isDisabled,
      '!cursor-pointer': isSelectable,

      '!bg-gray-200': isDisabled,
      '!bg-tertiary-200': isSelectable && isChecked,
      '!bg-tertiary-300':
        themeName !== 'VueFlow' && isSelectable && isActivated,
      '!bg-[#00E88E]/10': isSelectable && isActivated,
      '!bg-white': isSelectable && !isChecked && !isActivated,
    });
  };
  return rowClassName;
};
